import React from 'react'
import PropTypes from 'prop-types'
import get from 'lodash/get'
import gql from 'graphql-tag'
import { useQuery } from 'react-apollo'
import {
  Indicator,
  LocalizedDate,
  LocalizedText,
  Measure,
  Rows,
  Tooltip,
  TooltipTitle,
  LocalizedNumber,
  SpinLoader,
} from 'labguard-cdk'
import {
  HighLimitTempoIndicator,
  HighLimitIndicator,
  SetValueIndicator,
  LowLimitIndicator,
  LowLimitTempoIndicator,
  AcqChainNameIndicator,
  LocationIcon,
  ThermometerIcon,
} from 'src/components/common'
import { channelStatus } from 'src/entities'
import { ChannelIcon } from '../icons'

const query = gql`
  query channelInfoTooltip($channelId: ID!) {
    channel(id: $channelId) {
      id
      shortName
      localisation
      status
      lastMeasure {
        date
        value
      }
      lastThreshold {
        highTempo
        highLimitTempo
        highLimit
        setValue
        lowLimit
        lowLimitTempo
        lowTempo
      }
      acqChain {
        id
        name
        unit {
          id
          symbol
        }
      }
    }
  }
`

// eslint-disable-next-line react/prop-types
const TooltipBlock = ({ title, children }) => (
  <Rows>
    <TooltipTitle>{title}</TooltipTitle>
    {children}
  </Rows>
)

const TooltipContent = ({ channelId, showLastMeasure }) => {
  const { data: { channel = {} } = {}, loading } = useQuery(query, { variables: { channelId } })
  return (
    <Rows gap="1rem">
      {loading ? (
        <SpinLoader size="xs" color="white" />
      ) : (
        <>
          <TooltipBlock title={<LocalizedText id="WEB_CHANNEL" />}>
            <Indicator
              icon={ChannelIcon}
              separator
              subtitle={<LocalizedText id="WEB_CHANNEL_SHORT_NAME" />}
              title={channel.shortName || '-'}
              color="light"
            />
            <Indicator
              icon={LocationIcon}
              separator
              subtitle={<LocalizedText id="WEB_LOCALISATION" />}
              title={channel.localisation || '-'}
              color="light"
            />
          </TooltipBlock>
          {showLastMeasure && (
            <TooltipBlock title={<LocalizedText id="WEB_LAST_MEASUREMENT" />}>
              <Indicator
                icon={ThermometerIcon}
                separator
                title={
                  <Measure unit={get(channel, 'acqChain.unit.symbol')} color="light">
                    <LocalizedNumber value={get(channel, 'lastMeasure.value')} />
                  </Measure>
                }
                subtitle={
                  <LocalizedDate
                    value={get(channel, 'lastMeasure.date')}
                    day="2-digit"
                    month="long"
                    year="numeric"
                    hour="numeric"
                    minute="numeric"
                    second="numeric"
                  />
                }
                color="light"
                height="1.875rem"
              />
            </TooltipBlock>
          )}
          <TooltipBlock title={<LocalizedText id="WEB_THRESHOLDS" />}>
            <HighLimitTempoIndicator
              limit={get(channel, 'lastThreshold.highLimitTempo')}
              unit={get(channel, 'acqChain.unit.symbol')}
              tempo={get(channel, 'lastThreshold.highTempo')}
            />
            <HighLimitIndicator
              limit={get(channel, 'lastThreshold.highLimit')}
              unit={get(channel, 'acqChain.unit.symbol')}
            />
            <SetValueIndicator
              limit={get(channel, 'lastThreshold.setValue')}
              unit={get(channel, 'acqChain.unit.symbol')}
              color="light"
            />
            <LowLimitIndicator
              limit={get(channel, 'lastThreshold.lowLimit')}
              unit={get(channel, 'acqChain.unit.symbol')}
            />

            <LowLimitTempoIndicator
              limit={get(channel, 'lastThreshold.lowLimitTempo')}
              unit={get(channel, 'acqChain.unit.symbol')}
              tempo={get(channel, 'lastThreshold.lowTempo')}
            />
          </TooltipBlock>
          <TooltipBlock title={<LocalizedText id="WEB_ACQ_CHAIN" />}>
            {channel.acqChain && <AcqChainNameIndicator name={get(channel, 'acqChain.name')} />}
            {get(channel, 'status', [])
              .map(s => channelStatus[s])
              .map(({ icon, i18n, color }) => (
                <Indicator
                  key={i18n}
                  separator
                  title={<LocalizedText id={i18n} />}
                  icon={icon}
                  color={color}
                  height="1.875rem"
                />
              ))}
          </TooltipBlock>
        </>
      )}
    </Rows>
  )
}

TooltipContent.propTypes = {
  channel: PropTypes.shape({
    lastThreshold: PropTypes.object,
    acqChain: PropTypes.shape({
      unit: PropTypes.object,
    }),
  }),
  showLastMeasure: PropTypes.bool,
}

const ChannelInfoTooltip = ({ channelId, showLastMeasure, ...props }) => {
  return (
    <Tooltip overlay={() => <TooltipContent channelId={channelId} showLastMeasure={showLastMeasure} />} {...props} />
  )
}

ChannelInfoTooltip.defaultProps = {
  triggers: ['hover', 'click'],
  showLastMeasure: false,
}

ChannelInfoTooltip.propTypes = {
  channelId: PropTypes.string,
  showLastMeasure: PropTypes.bool,
  ...Tooltip.propTypes,
  ...TooltipContent.propTypes,
}

export default ChannelInfoTooltip
